import React, { useState, useEffect } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import ExcelTable from '../../components/admin/ExcelTable';
import { formatDate } from '../../utils/dateHelper';

const ArchiveView = ({ backendUrl }) => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  // Track AdminNav mode
  const [navMode, setNavMode] = useState(() => {
    return localStorage.getItem('adminNavMode') || (window.innerWidth < 1024 ? 'peek' : 'expanded');
  });

  // Listen for AdminNav toggle events
  useEffect(() => {
    const handleNavToggle = (e) => {
      if (e.detail.mode) {
        setNavMode(e.detail.mode);
      } else {
        setNavMode(e.detail.isCollapsed ? 'collapsed' : 'expanded');
      }
    };
    window.addEventListener('adminNavToggle', handleNavToggle);
    return () => window.removeEventListener('adminNavToggle', handleNavToggle);
  }, []);

  useEffect(() => {
    fetchArchived();
  }, []);

  const fetchArchived = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections?status=archived&limit=10000`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();

      if (data.success) {
        setInspections(data.inspections || []);
      }
    } catch (error) {
      console.error('Error fetching archived:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (inspectionIds) => {
    const confirmed = window.confirm(
      `Restore ${inspectionIds.length} inspection(s) to pending status?`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/inspections/bulk-restore`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_ids: inspectionIds })
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ ${data.restored_count} inspection(s) restored to pending`);
        fetchArchived();
      } else {
        alert(`❌ Failed to restore inspections`);
      }
    } catch (error) {
      console.error('Error restoring:', error);
      alert(`❌ Failed to restore: ${error.message}`);
    }
  };

  const columns = [
    {
      key: 'thumbnail_url',
      label: 'Image',
      sortable: false,
      render: (url, row) => (
        <img
          src={url}
          alt="Tag"
          className="w-16 h-16 object-cover rounded cursor-pointer hover:ring-2 hover:ring-[var(--pbfs-blue)]"
          onClick={() => setSelectedImage(row.image_url)}
          loading="lazy"
        />
      )
    },
    {
      key: 'all_dates',
      label: 'All Dates',
      sortable: false,
      render: (val, row) => (
        <div className="flex flex-col gap-1 text-xs">
          <div className="px-1 py-0.5">
            <span className="text-white/50">Captured:</span>{' '}
            <span className="text-white/80">
              {row.capture_date ? formatDate(row.capture_date, true) : '—'}
            </span>
          </div>
          <div className="px-1 py-0.5">
            <span className="text-white/50">Archived:</span>{' '}
            <span className="text-white/80">
              {row.archived_at ? formatDate(row.archived_at, true) : '—'}
            </span>
          </div>
        </div>
      )
    },
    {
      key: 'site_location',
      label: 'Site Location',
      sortable: false,
      render: (val, row) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <div className="font-semibold text-white/90">{row.business_name || '—'}</div>
          <div className="text-white/70">{row.site_street_address || '—'}</div>
          <div className="text-white/70">
            {[row.site_city, row.site_state, row.site_zip].filter(Boolean).join(', ') || '—'}
          </div>
        </div>
      )
    },
    {
      key: 'assigned_branch',
      label: 'Nearest Branch',
      sortable: true,
      render: (val) => val?.name || '—'
    },
    {
      key: 'submitted_by',
      label: 'Submitted By',
      sortable: true
    }
  ];

  const getNavOffset = () => {
    switch (navMode) {
      case 'peek': return 'ml-4 w-[calc(100%-1rem)]';
      case 'collapsed': return 'ml-20 w-[calc(100%-5rem)]';
      default: return 'ml-64 w-[calc(100%-16rem)]';
    }
  };

  return (
    <div className={`h-screen overflow-hidden bg-gradient-to-br from-[#00314F] via-[#003d5c] to-[#00314F] text-white ${getNavOffset()} overflow-x-hidden`}>
      <AdminNav />

      <div className="pt-6 px-6 h-full overflow-hidden flex flex-col max-w-full">
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">🗄️ Archive</h1>
          <p className="text-white/60 text-xs sm:text-sm">Archived inspection submissions</p>
        </div>

        <div className="flex-1 bg-[var(--pbfs-navy)]/40 backdrop-blur-sm rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
          <ExcelTable
            data={inspections}
            columns={columns}
            expandableColumns={[]}
            onDelete={handleRestore}
            currentStatusFilter="archived"
            loading={loading}
            editingCell={null}
            setEditingCell={() => { }}
            editValue=""
            setEditValue={() => { }}
            onCellSave={() => { }}
          />

          {/* Custom button text override */}
          <style>{`
            /* Override delete button to say "Restore" */
          `}</style>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <img src={selectedImage} alt="Full size" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
};

export default ArchiveView;

